/** @jsxImportSource @emotion/react */
import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Tab, Tabs } from '@mui/material';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import { Code } from 'models/common/CommonCode';
import { useCommonModal } from 'hooks/useCommonModal';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { useLoading } from 'components/process/Loading';
import { MdIvesCondition, MdIvesPrjInfo, TabMenu } from 'models/md/MdIves';
import { useNavigate } from 'react-router-dom';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { tabs } from 'components/layouts/Tab';
import IvesAtc from './atcMgt/IvesAtc';
import DtlIsptMatr from './atcMgt/DtlIsptMatr';

const MdIvesAtcMgtPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();

  const initCondition: MdIvesCondition = {
    elmPrjId: '',
    elmPrjNm: '',
    bildPlntCd: '',
    bildPlntCds: [],
    elmPrjStatCd: '',
    custNm: '',
    mdlNm: '',
    dataInsUserId: '',
  };

  const gridRef = useRef();
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [elmPrjStatCd, setElmPrjStatCd] = useState<Code[]>([]);
  const [condition, setCondition] = useState<MdIvesCondition>({ ...initCondition });
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);

  const tabMenus: TabMenu[] = [
    { id: 'IVES', name: t('setup.label.투자항목', '투자항목') },
    { id: 'DETAIL', name: t('setup.label.상세 점검사항', '상세 점검사항') },
  ];

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('BILD_PLNT_CD');
    const elmPrjStatCd = await getCommonCodeNames('MD_ELM_PRJ_STAT_CD');
    setBildPlntCode(bildPlntCode);
    setElmPrjStatCd(elmPrjStatCd);
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const btnSearch = () => {
    // search
  };

  const btnExcelExport = () => {
    // excel download
  };

  useEffect(() => {
    initCodes();
    btnSearch();
  }, []);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('setup.label.공정', '공정'))}</label>
                <ComboBox
                  options={bildPlntCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.bildPlntCd}
                  onChange={(value) => {
                    handleOnChange('bildPlntCd', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.설비군', '설비군'))}</label>
                <CustomInputWithSearch
                  name="elmPrjNm"
                  className="find"
                  value={condition.elmPrjNm}
                  onChange={(e) => handleOnChange('elmPrjNm', e.target.value)}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.셀 구분', '셀 구분')}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                  }}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{t('setup.label.투자항목', '투자항목')}</label>
                <CustomInputWithSearch
                  name="custNm"
                  className="find"
                  value={condition.custNm}
                  onChange={(e) => handleOnChange('custNm', e.target.value)}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.단가기준', '단가기준')}</label>
                <ComboBox
                  options={bildPlntCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.bildPlntCd}
                  onChange={(value) => {
                    handleOnChange('bildPlntCd', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.등록자', '등록자')}</label>
                <CustomInputWithSearch
                  name="dataInsUserId"
                  className="find"
                  value={condition.dataInsUserId}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={() => btnSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <div css={tabs.wrap} className="mt-6">
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`td-ives-cmpu-tab-${idx}`}
              aria-controls={`td-ives-cmpu-tabpanel-${idx}`}
            />
          ))}
        </Tabs>
      </div>
      <TabPanel value={currentTabIndex} index={0}>
        <IvesAtc />
      </TabPanel>
      <TabPanel value={currentTabIndex} index={1}>
        <DtlIsptMatr />
      </TabPanel>
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`setup-schedule-tabpanel-${index}`}
      className="mb-5"
    >
      <div>{children}</div>
    </div>
  );
}

export default MdIvesAtcMgtPage;
